/**
 * Lagging-conversations panel (CHAP-302) - fundraising surface, cabinet+.
 *
 * Prospects whose last outreach touch is older than the tenant's threshold
 * (default 21 days) with no scheduled follow-up, sorted capacity tier desc
 * then days lagging desc so the Cabinet works the biggest stalled asks
 * first. One-click "Log follow-up" records an outreach event and drops the
 * row off the list. Hidden entirely below cabinet (the endpoint is
 * cabinet-gated; the hook self-disables).
 */
import { CAPACITY_TIERS } from '../../../shared/pipeline.js';
import { useIsCabinetPlus, useLaggingConversations, useLogFollowUp } from '../../hooks/useLaggingConversations.js';

export function LaggingConversations({ slug }: { slug: string }) {
  const isCabinetPlus = useIsCabinetPlus();
  const { data, isLoading, error } = useLaggingConversations(slug);
  const followUp = useLogFollowUp(slug);

  if (!isCabinetPlus) return null;

  const items = data?.items ?? [];
  const thresholdDays = data?.thresholdDays ?? 21;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
      <div className="flex items-center gap-2 flex-wrap min-w-0 mb-1">
        <ConversationIcon />
        <h3 className="text-navy font-semibold text-sm sm:text-base">Lagging Conversations</h3>
        {items.length > 0 && (
          <span className="bg-orange text-white text-[11px] font-bold rounded-full px-2 py-0.5 flex-shrink-0">
            {items.length}
          </span>
        )}
      </div>
      <p className="text-neutral text-xs sm:text-sm mb-3">
        No touch in {thresholdDays}+ days and no scheduled follow-up. Work the biggest asks first.
      </p>

      {isLoading && <p className="text-neutral text-sm py-4 text-center">Loading lag list…</p>}
      {error && <p className="text-red-700 text-sm py-4 text-center">Error: {error.message}</p>}

      {!isLoading && !error && items.length === 0 && (
        <p className="text-neutral text-sm py-4 text-center">
          No lagging conversations. Every active prospect has a recent touch or a scheduled follow-up.
        </p>
      )}

      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map((item) => {
            const tier = CAPACITY_TIERS.find((t) => t.key === item.capacityTier);
            const pending = followUp.isPending && followUp.variables?.recordId === item.recordId;
            return (
              <div
                key={item.recordId}
                className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2.5 min-w-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="text-navy text-sm font-semibold truncate">{item.name}</span>
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
                      style={{
                        color: tier?.color ?? '#64748b',
                        backgroundColor: `${tier?.color ?? '#64748b'}1f`,
                      }}
                    >
                      {tier?.label ?? item.capacityTier}
                    </span>
                  </div>
                  <p className="text-neutral text-[11px] sm:text-xs mt-0.5 truncate">
                    Last touch {new Date(item.lastTouchAt).toLocaleDateString()}
                    {item.lastTouchAction ? ` · ${item.lastTouchAction}` : ''}
                    {item.ownerName ? ` · owner: ${item.ownerName}` : ' · no owner'}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-orange text-base sm:text-lg font-bold leading-tight">{item.daysLagging}</p>
                    <p className="text-neutral text-[10px] uppercase tracking-wide">days</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => followUp.mutate({ recordId: item.recordId })}
                    disabled={pending}
                    className="bg-navy text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-40 whitespace-nowrap"
                  >
                    {pending ? 'Logging…' : 'Log follow-up'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {followUp.isError && (
        <p className="text-red-700 text-xs mt-2">Follow-up failed: {followUp.error.message}</p>
      )}
    </div>
  );
}

function ConversationIcon() {
  return (
    <svg className="w-5 h-5 flex-shrink-0 text-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
    </svg>
  );
}
